import React, {useEffect, useRef, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import PageCard from '../components/PageCard';
import PeopleEditor from '../components/PeopleEditor';
import StatusMessage from '../components/StatusMessage';
import {supabase} from '../lib/supabase';

const {FiLock, FiSave} = FiIcons;

function SettingsPage({config, people, onSaved}) {
  const navigate = useNavigate();
  const prompted = useRef(false);
  const [form, setForm] = useState(config);
  const [personRows, setPersonRows] = useState(people);
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    setForm(config);
    setPersonRows(people);
  }, [config, people]);

  useEffect(() => {
    if (prompted.current) return;

    prompted.current = true;
    const password = window.prompt('Enter the admin password to open Settings:');

    if (password === config.admin_password) {
      setUnlocked(true);
    } else {
      navigate('/', {replace: true});
    }
  }, [config.admin_password, navigate]);

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setStatus({});

    const validPeople = personRows.filter((person) => person.name.trim());
    const keptIds = validPeople
      .filter((person) => person.id)
      .map((person) => person.id);
    const removedIds = people
      .filter((person) => !keptIds.includes(person.id))
      .map((person) => person.id);
    const existing = validPeople
      .filter((person) => person.id)
      .map((person, index) => ({
        id: person.id,
        name: person.name.trim(),
        sort_order: index
      }));
    const added = validPeople
      .filter((person) => !person.id)
      .map((person, index) => ({
        name: person.name.trim(),
        sort_order: existing.length + index
      }));

    const configResult = await supabase
      .from('config')
      .update({
        instance_name: form.instance_name,
        logo_url: form.logo_url,
        compact_logo_url: form.compact_logo_url,
        app_password: form.app_password,
        admin_password: form.admin_password
      })
      .eq('id', form.id);

    let error = configResult.error;

    if (!error && existing.length) {
      error = (await supabase.from('people').upsert(existing)).error;
    }

    if (!error && added.length) {
      error = (await supabase.from('people').insert(added)).error;
    }

    if (!error && removedIds.length) {
      error = (await supabase.from('people').delete().in('id', removedIds)).error;
    }

    if (error) {
      setStatus({type: 'error', message: error.message});
    } else {
      await onSaved(form.app_password);
      setStatus({message: 'Configuration saved.'});
    }

    setSaving(false);
  }

  if (!unlocked) {
    return <div className="loading-panel">Checking admin access…</div>;
  }

  return (
    <PageCard
      title="Edit Configuration"
      description="Manage branding, access, and the people who can log hours."
    >
      <form className="settings-form" onSubmit={save}>
        <label>
          Instance Name
          <input
            value={form.instance_name || ''}
            onChange={(e) => setForm({...form, instance_name: e.target.value})}
            required
          />
        </label>
        <label>
          Logo URL
          <input
            type="url"
            value={form.logo_url || ''}
            onChange={(e) => setForm({...form, logo_url: e.target.value})}
            placeholder="https://example.com/logo.png"
          />
        </label>
        <label>
          Compact Logo URL (Mobile)
          <input
            type="url"
            value={form.compact_logo_url || ''}
            onChange={(e) => setForm({...form, compact_logo_url: e.target.value})}
            placeholder="https://example.com/compact-logo.png"
          />
          <small>Used on mobile devices instead of the main logo</small>
        </label>
        <label>
          Login Password
          <input
            type="text"
            value={form.app_password || ''}
            onChange={(e) => setForm({...form, app_password: e.target.value})}
            required
          />
          <small>The shared password used to log in to this app</small>
        </label>
        <label>
          Admin Password
          <div className="input-with-icon">
            <SafeIcon icon={FiLock} />
            <input
              type="password"
              value={form.admin_password || ''}
              onChange={(e) => setForm({...form, admin_password: e.target.value})}
              autoComplete="new-password"
              required
            />
          </div>
          <small>Required to open this Settings page — keep this private</small>
        </label>
        <PeopleEditor people={personRows} onChange={setPersonRows} />
        <div className="form-actions">
          <StatusMessage {...status} />
          <button className="primary-button" disabled={saving}>
            <SafeIcon icon={FiSave} />
            {saving ? 'Saving…' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </PageCard>
  );
}

export default SettingsPage;